/**
 * GreetingHero — date eyebrow + greeting H1 + (desktop) CTAs.
 *
 * Two text rows + (desktop only) Customize + Add widget buttons. Ports
 * `dashboard-shell.jsx::GreetingHero` from the canonical handoff.
 *
 * SCRUM-58 visual-fidelity round (2026-05-10):
 *  - Eyebrow weight 600 (was 700/font-bold), letter-spacing 0.6 (was
 *    1.2px). `·` divider gets `opacity: 0.5`.
 *  - Customize/Add widget now route through `<Btn/>` primitive with
 *    sliders + plus icons (was: ad-hoc inline buttons with text-only
 *    labels and a literal `+` glyph).
 *  - `org.status?: string` removed from the props (stale after the
 *    codex-round-1 status-dot removal).
 *  - `user` prop reserved for future `name?` extension; v1 still passes
 *    `firstNameOf(undefined)` → "there".
 */
import * as React from 'react';
import { firstNameOf, formatDateLine, greetingFor } from '../helpers/greeting.js';
import { Button } from '@sparta/ui-kit';

export interface GreetingHeroProps {
  user: { id: string; email: string; name?: string };
  org: { name: string };
  now: Date;
  mode?: 'pro' | 'lite';
  compact?: boolean;
  onCustomize?: () => void;
  onAddWidget?: () => void;
  /** Locale for date formatting; defaults to host. */
  locale?: string;
}

export function GreetingHero({
  user,
  org,
  now,
  compact = false,
  onCustomize,
  onAddWidget,
  locale,
}: GreetingHeroProps): React.ReactElement {
  // Future hook: when `useCurrentUser` widens to surface `name`, swap
  // `firstNameOf(undefined)` for `firstNameOf(user.name)`. Today v1
  // intentionally passes `undefined` → fallback "there".
  void user;
  const greeting = greetingFor(now);
  const firstName = firstNameOf(undefined);
  const dateLine = formatDateLine(now, locale);

  const headerMarginClass = compact ? 'mb-4' : 'mb-6';
  const h1SizeClass = compact ? 'text-[26px]' : 'text-[34px]';
  return (
    <header
      aria-labelledby="greeting-heading"
      className={`flex items-end gap-4 flex-wrap ${headerMarginClass}`}
    >
      <div className="min-w-0 flex-1">
        <p
          className="text-xs font-semibold text-[var(--fg-tertiary)] tracking-[0.6px] uppercase mb-1.5 flex items-center gap-2 flex-wrap"
          // ADR frontend-0002 exemption 3: CSS margin shorthand reset.
          // Tailwind classes don't zero the default browser left/right margins
          // on a <p>; the four-sided shorthand keeps the eyebrow flush.
          style={{ margin: '0 0 6px 0' }}
        >
          <span>{dateLine}</span>
          <span aria-hidden="true" className="opacity-50">·</span>
          <span>{org.name}</span>
        </p>
        <h1
          id="greeting-heading"
          className={`${h1SizeClass} font-bold m-0 text-[var(--fg-primary)] tracking-[-0.6px] leading-[1.15]`}
        >
          {greeting}, {firstName}
        </h1>
      </div>
      {!compact ? (
        <div className="flex gap-2 shrink-0">
          <Button variant="secondary" icon="sliders" onClick={onCustomize}>
            Customize
          </Button>
          <Button variant="primary" icon="plus" onClick={onAddWidget}>
            Add widget
          </Button>
        </div>
      ) : null}
    </header>
  );
}
